<template>
    <v-card variant="outlined">
        <div class="d-flex wrapper">
            <v-img
                :alt="product.name"
                src="/insurance.jpg"
                width="200"
                max-width="200"
                height="200"
                lazy-src="/placeholder.png"
                tile
                eager
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row
                        v-show="!imgError && product.offers[0].mainImageUrl"
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                    >
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div class="ma-2 flex-grow-1 flex-shrink-1 d-flex text-truncate">
                <div class="mb-2 font-weight-medium text-truncate text-primary">
                    <span :title="product.name">
                        {{ product.name }}
                    </span>
                    <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" />
                </div>
                <div v-if="product.shortDescription" v-html="product.shortDescription" />
            </div>
            <div class="ma-4 price-col flex-shrink-0 text-right">
                <slot name="price" :offer="product.offers[0]">
                    <template v-if="!minPriceOffer.soldOut">
                        <div data-cy="client-price" class="font-weight-medium">
                            {{ $t('from') }} {{ $filters.price(minPriceOffer.price) }}
                        </div>
                        <discount-label :price="product.offers[0].price" />
                        <commission-amount :commission="minPriceOffer.price.commission" />
                        <v-btn
                            variant="flat"
                            block
                            color="primary"
                            class="my-2"
                            @click="selectForBooking(product.offers[0])"
                        >
                            {{ $t('book') }}
                        </v-btn>
                    </template>
                </slot>
                <div class="text-primary cursor-pointer text-no-wrap" @click="expanded = !expanded">
                    <span>
                        {{ $t('tours.detailed_info') }}
                    </span>
                    <v-icon color="primary">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </div>
            </div>
        </div>
        <v-expand-transition>
            <v-list v-if="expanded">
                <template v-for="(offer, index) in product.offers" :key="offer.offerKey">
                    <v-list-item :data-cy="`insurance-offer-card-${index}`">
                        <div class="align-self-start">
                            <div
                                class="wrap-words d-flex align-center text-subtitle-1 font-weight-medium cursor-pointer mb-1"
                                @click="offerInfo(offer)"
                            >
                                <v-icon size="small" start>mdi-information-outline</v-icon>
                                <span class="font-weight-bold">
                                    {{ offer.planName }}
                                </span>
                            </div>
                            <div class="text-caption">
                                <span v-if="isAvailable">
                                    <v-icon :title="$t('available')" size="small" start class="text-success">
                                        mdi-checkbox-marked-circle
                                    </v-icon>
                                    <span class="text-success" v-text="$t('available')" />
                                </span>
                                <span v-else>
                                    <v-icon start :title="$t('on_request')" size="small" class="text-warning">
                                        mdi-alert-circle
                                    </v-icon>
                                    <span class="text-warning" v-text="$t('on_request')" />
                                </span>
                                <div v-if="offer.price">
                                    <conditions-label
                                        v-if="offer"
                                        :offer-key="offer.offerKey"
                                        :price="offer.price"
                                        @conditions="showConditionsInfo(offer)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="price-col ms-auto d-flex flex-column align-center">
                            <slot name="price" :offer="offer">
                                <template v-if="!offer.soldOut">
                                    <v-btn
                                        color="primary"
                                        class="my-2"
                                        block
                                        variant="flat"
                                        data-cy="client-price"
                                        @click="selectForBooking(offer)"
                                    >
                                        {{ $filters.price(offer.price) }}
                                    </v-btn>
                                    <div class="text-caption text--secondary">
                                        {{
                                            `${touristsTotal} ${$t('tourist', touristsTotal)},
                                             ${searchRequest.daysCount} ${$t('day', searchRequest.daysCount)}`
                                        }}
                                    </div>
                                    <commission-amount :commission="offer.price.commission" />
                                    <net-amount :search-request="searchRequest" :price="offer.price" />
                                </template>
                                <sold-out-modal v-else />
                            </slot>
                        </div>
                    </v-list-item>
                    <v-divider v-if="index !== product.offers.length - 1" :key="index" />
                </template>
            </v-list>
        </v-expand-transition>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import ConditionsLabel from '~/components/snippets/ConditionsLabel.vue'
    import {EventBus, SHOW_INSURANCE_CONDITIONS_INFO, SHOW_INSURANCE_INFO_MODAL} from '@/utils/event-bus'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import SoldOutModal from '~/components/snippets/SoldOutModal.vue'

    @Component({
        components: {
            SoldOutModal,
            DiscountLabel,
            ConditionsLabel,
            CommissionAmount,
            NetAmount,
            SupplierNameLabel,
        },
    })
    export class InsuranceOfferCard extends Vue {
        @Prop({required: true}) product

        imgError = false
        expanded = false

        selectForBooking(offer) {
            insuranceStore.ADD_TO_BASKET(offer.offerKey)
            this.$router.push({
                name: 'insuranceBooking',
                query: {offerKey: offer.offerKey},
            })
        }

        offerInfo(offer) {
            EventBus.$emit(SHOW_INSURANCE_INFO_MODAL, {offer})
        }

        showConditionsInfo(offer) {
            EventBus.$emit(SHOW_INSURANCE_CONDITIONS_INFO, {searchRequest: this.searchRequest, offer})
        }

        onImgError() {
            this.imgError = true
            return false
        }

        get minPriceOffer() {
            return minPriceOffer(this.product)
        }

        get isAvailable() {
            // TODO: availability
            return true
        }

        get mainImg() {
            //TODO: img for insurance
            return ''
        }

        get searchRequest() {
            return insuranceStore.searchRequest
        }

        get touristsTotal() {
            return insuranceRuntimeStore.touristsTotal(this.searchRequest)
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default InsuranceOfferCard
</script>

<style lang="scss" scoped>
    :deep(.insurance-description p) {
        margin-bottom: 0;
    }

    .price-col {
        width: 180px;
    }

    //TODO Remove after layout redesign
    @media (max-width: 1440px) {
        .price-col {
            width: 130px;
        }
    }
</style>
